import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'
import type { ILeadMissingFieldDTO } from '@user/store/lead/typings'
import { groupMissingFields } from '@user/features/PendingLeadAutoReview/utils/groupMissingFields'
import type { MissingFieldGroup } from '@user/features/PendingLeadAutoReview/typings'

/** Lead form fields the shared Pending Lead renderers do not cover. */
export type LeadExtraFieldKey =
  | 'preferredMethodOfContact'
  | 'preferredTimeOfContact'
  | 'nextActionDate'

export interface LeadCompanyRequiredFieldsModel {
  /** Missing items the shared renderers, validator and input builder understand. */
  fields: IAutoReviewMissingField[]
  groups: MissingFieldGroup[]
  extraKeys: LeadExtraFieldKey[]
}

const EXTRA_KEYS: LeadExtraFieldKey[] = [
  'preferredMethodOfContact',
  'preferredTimeOfContact',
  'nextActionDate',
]

/**
 * Splits the server's missing company-required fields into what the Pending Lead renderers can
 * handle and the three lead-only extras.
 */
export function mapMissingFieldsToForm(
  missing: ILeadMissingFieldDTO[],
): LeadCompanyRequiredFieldsModel {
  const extraKeys = EXTRA_KEYS.filter((key) =>
    missing.some((field) => !field.isCustomField && field.fieldKey === key),
  )

  const fields: IAutoReviewMissingField[] = missing
    .filter(
      (field) =>
        field.isCustomField ||
        !EXTRA_KEYS.includes(field.fieldKey as LeadExtraFieldKey),
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      fieldId: field.fieldId,
      fieldName: field.fieldName,
      isCustomField: field.isCustomField,
      isAdditionalField: !field.isCustomField,
    }))

  return {
    fields,
    groups: groupMissingFields(fields),
    extraKeys,
  }
}
